import { toUrlFormat } from '../treeVisitors';
import { getElementIdFromPathName } from '../../client/components/PageElements/CustomLink/Link/utils';
import { mdHeadingPrefix } from '../../client/components/NavigationMenu/utils';

export const isAbsoluteUrl = (url) => /^https?:\/\//i.test(url);

export const MENU_EDITOR_PATH = '/menu-editor';
export const isMenuEditorPath = (path) => path === MENU_EDITOR_PATH;

export const parseQueryString = (query = ''): { [key: string]: string } => {
  const map = {};
  query
    .replace('?', '')
    .split('&')
    .forEach((q) => {
      const [key, value] = q.split('=');
      map[key] = value;
    });
  return map;
};

export const getPathnameSuffix = (pathname) =>
  removeTrailingSlashes(pathname)?.replace(/.*\//, '');

const getMarkdownHeadingId = (hash) => hash.replace('#', '');

export const scrollToActiveElementOnMount = (location) => {
  const elementId = location.hash
    ? getMarkdownHeadingId(location.hash)
    : getElementIdFromPathName(location.pathname);

  const activeElementSection = document.getElementById(decodeURI(elementId));

  activeElementSection && activeElementSection.scrollIntoView();
};

export const removeRouterBasename = (url, routerBasename) => {
  const pattern = new RegExp(`^${routerBasename}`);
  return url.replace(pattern, '');
};

export const fromRelativeUrlToAbsoluteUrl = (relativeUrl, origin, store) =>
  `${origin}${store.storePath}${relativeUrl}`;

export const pathToLink = (path: string): string => {
  const [category, ...postfix] = path.split('/');
  const article =
    postfix.length > 0 ? `/${postfix.map(toUrlFormat).join('/')}` : '';
  return `/${toUrlFormat(category)}${article}`;
};

export const typeToLink = (type: string): string => {
  const path = type.replace(/\./g, '/');
  return pathToLink(path);
};

export const isLocalViewer = (location): boolean => {
  const queryObject = parseQueryString(location.search);
  return !!queryObject.localViewerId;
};

export const getLocalViewerId = (location): string => {
  const queryObject = parseQueryString(location.search);
  return queryObject.localViewerId;
};

export const isBranch = (location): boolean => {
  const queryObject = parseQueryString(location.search);
  return !!queryObject.branch;
};

export const isBackOffice = (location): boolean => {
  const queryObject = parseQueryString(location.search);
  return !!queryObject.backoffice;
};

export const isHashLink = (link) => link.startsWith('#');

export const getPathnameAndHashFromHref = (href: string) => href.split(/(?=#)/);

export const getHrefObject = (href) => {
  const [pathname, hash] = getPathnameAndHashFromHref(href);
  return { pathname, hash };
};

export const removeStartingSlash = (url) => url && url.replace(/^\//, '');
export const removeStartingProtocol = (url) =>
  url && url.replace(/^(https:\/\/|http:\/\/)/, '');

export const getPathnameFromUrl = (url) => {
  const urlWithoutStartingProtocol = removeStartingProtocol(url);
  const hasPathname = urlWithoutStartingProtocol.split('/')[1];
  if (!hasPathname) {
    return '/';
  }
  const isRelativeUrl = url.startsWith('/');
  return isRelativeUrl
    ? url
    : `/${removeStartingProtocol(url).split(/\/(.+)/)[1]}`;
};

export const getMdHeadingId = (path, headingTextForId) => {
  const prefixForMdHeading = path.replace(/\//g, '_');
  return mdHeadingPrefix(prefixForMdHeading, headingTextForId);
};

export const isLinkToOutsideTheViewer = (link, windowHost, routerBasename) => {
  const [urlHost] = removeStartingProtocol(link).split('/');
  const linkPathname = getPathnameFromUrl(link);
  return (
    isAbsoluteUrl(link) &&
    (!linkPathname.startsWith(routerBasename) || urlHost !== windowHost)
  );
};

export const getArticleNameAndCategoryFromArticleLink = (link) => {
  const linkWithoutStartingSlash = removeStartingSlash(link);
  const [categoryName, ...articleParts] =
    (linkWithoutStartingSlash && linkWithoutStartingSlash.split('/')) || [];
  const articleName =
    articleParts.length > 0 ? articleParts.join('.') : undefined;
  return { categoryName, articleName };
};

export const isMailUrl = (url) =>
  typeof url === 'string' && url.startsWith('mailto:');

export function removeTrailingSlashes(url) {
  return url?.replace(/\/*$/, '');
}
